import { ArrowRight, Instagram, MessageSquare, Clock, Flame } from "lucide-react";
import { buildBookingSmsHref } from "../lib/bookingSms";

const INSTAGRAM_PROFILE_URL = "https://www.instagram.com/jesterwerks.gta/";

// Stock counter. Number of remaining October/pre-winter spots.
// Update this manually as spots fill up. Keep it believable (3-8 range).
const SPOTS_LEFT = 4;
const SPOTS_TOTAL = 12;

export default function UrgencyCTA() {
  const filled = SPOTS_TOTAL - SPOTS_LEFT;
  const percent = Math.round((filled / SPOTS_TOTAL) * 100);

  return (
    <section
      className="relative py-24 md:py-32 bg-[#08010f] border-y border-jester/30 overflow-hidden"
      data-testid="urgency-cta-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#3C1478_0%,transparent_60%)] opacity-40" />
      <div className="absolute -top-40 -right-20 w-[600px] h-[600px] rounded-full bg-jester-purple/20 blur-[140px]" />
      <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-acid/10 blur-[140px]" />

      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
        {/* Stock counter. winter urgency */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-400/40 bg-red-500/10 backdrop-blur-sm mb-8"
          data-testid="stock-counter-pill"
        >
          <Flame className="w-3.5 h-3.5 text-red-400 animate-pulse" />
          <span className="text-xs font-label uppercase tracking-[0.3em] text-red-300">
            Only {SPOTS_LEFT} of {SPOTS_TOTAL} pre-winter spots left
          </span>
        </div>

        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92]">
          Beat the first freeze.<br />
          <span className="text-jester">Lock it in for 5 years.</span>
        </h2>

        <p className="mt-8 text-white/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
          Text us a photo of your car right now. We reply within an hour
          with a firm quote, an October or early-November drop-off date,
          and the exact before-and-after we are going to shoot on your
          vehicle. Once the first plow hits, this rate is gone until spring.
        </p>

        {/* Visual stock bar */}
        <div className="mt-10 max-w-md mx-auto" data-testid="stock-progress">
          <div className="flex items-center justify-between mb-2 text-xs uppercase tracking-[0.2em] font-label">
            <span className="text-white/50">Pre-winter spots</span>
            <span className="text-red-300">{filled}/{SPOTS_TOTAL} booked</span>
          </div>
          <div className="h-3 rounded-full bg-white/8 overflow-hidden border border-white/10">
            <div
              className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full transition-all duration-1000"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* Primary CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={buildBookingSmsHref()}
            data-testid="urgency-text-cta"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 h-16 rounded-full bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] text-base uppercase transition-all shadow-[0_0_60px_rgba(168,224,0,0.55)]"
          >
            <MessageSquare className="w-5 h-5" />
            Text Us Now. $550
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href={INSTAGRAM_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="urgency-instagram-cta"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 h-16 rounded-full border border-jester bg-jester-purple/20 hover:bg-jester-purple/40 text-white font-label tracking-[0.2em] text-base uppercase transition-all"
          >
            <Instagram className="w-5 h-5" />
            DM on Instagram
          </a>
        </div>

        {/* Trust bar */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-white/8">
          {[
            { k: "1hr", v: "Avg reply time" },
            { k: "250+", v: "Google 5-star reviews" },
            { k: "24hr", v: "Firm quote turnaround" },
            { k: "5yr", v: "Written warranty" },
          ].map((t, i) => (
            <div key={i} data-testid={`urgency-trust-${i}`}>
              <div className="font-stencil text-3xl md:text-4xl text-acid">
                {t.k}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] text-white/55 font-label">
                {t.v}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 inline-flex items-center gap-2 text-sm text-white/50">
          <Clock className="w-4 h-4" />
          <span>October &amp; early-November drop-off dates only. Rate ends at first snow.</span>
        </div>
      </div>
    </section>
  );
}
